import type { ApiAgreementListingRequestBody } from '@/types/agreements';
import { queryClient as defaultQueryClient } from '@/plugins/vueQuery';
import { queryKeys } from '@/utils/constants';
import type { QueryClient } from '@tanstack/vue-query';

const MODULE_KEY = 'agreements';

/** Query Key Getters */

/**
 * Cache key for the paginated agreements list (`getAgreementsList` API).
 * @see ApiAgreementListingRequestBody
 */
export const getAgreementsListQueryKey = (
  teamTwrn?: string | null,
  requestBody?: ApiAgreementListingRequestBody | null,
) => {
  const key: unknown[] = [MODULE_KEY, queryKeys.GET_AGREEMENTS_LIST];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (requestBody !== undefined && requestBody !== null) {
    if (!teamTwrn) {
      console.warn(
        '[getAgreementsListQueryKey] requestBody was provided without teamTwrn. teamTwrn is required when requestBody is passed.',
        {
          requestBody,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(requestBody);
  }

  return key;
};

/**
 * Cache key for the unpaginated agreements list used by select/dropdown UIs
 * (`getAgreements` API). Distinct from `getAgreementsListQueryKey`, which backs the
 * paginated `getAgreementsList` API used by the agreements table — both must be
 * invalidated together via `invalidateAgreementsLists`.
 */
export const getAgreementsQueryKey = (teamTwrn?: string | null) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.GET_AGREEMENTS];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  return key;
};

/**
 * Cache key for a single agreement's details (`getAgreementDetails` API).
 */
export const getAgreementDetailsQueryKey = (
  teamTwrn?: string | null,
  agreementTwrn?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.GET_AGREEMENT_DETAILS];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (agreementTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAgreementDetailsQueryKey] agreementTwrn was provided without teamTwrn. teamTwrn is required when agreementTwrn is passed.',
        {
          agreementTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(agreementTwrn);
  }

  return key;
};

/**
 * Cache key for a single contract template (`getContractTemplate` API).
 */
export const getContractTemplateQueryKey = (
  teamTwrn?: string | null,
  contractTemplateTwrn?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.GET_CONTRACT_TEMPLATE];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (contractTemplateTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getContractTemplateQueryKey] contractTemplateTwrn was provided without teamTwrn. teamTwrn is required when contractTemplateTwrn is passed.',
        {
          contractTemplateTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(contractTemplateTwrn);
  }

  return key;
};

/**
 * Cache key for a single athlete's contract (`getAthleteContract` API).
 * Shared by ActionsCell (lazy refetch on click) and AgreementDrawer (query-driven) so both
 * read/invalidate the same cache entry.
 */
export const getAthleteContractQueryKey = (
  teamTwrn?: string | null,
  agreementTwrn?: string | null,
  agreementAthleteTwrn?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.GET_ATHLETE_CONTRACT];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (agreementTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAthleteContractQueryKey] agreementTwrn was provided without teamTwrn. teamTwrn is required when agreementTwrn is passed.',
        {
          agreementTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(agreementTwrn);
  }

  if (agreementAthleteTwrn) {
    if (!agreementTwrn) {
      console.warn(
        '[getAthleteContractQueryKey] agreementAthleteTwrn was provided without agreementTwrn. agreementTwrn is required when agreementAthleteTwrn is passed.',
        {
          agreementAthleteTwrn,
          agreementTwrn,
        },
      );
      return key;
    }
    key.push(agreementAthleteTwrn);
  }

  return key;
};

/**
 * Cache key for athletes on an agreement (`getAgreementAthletes` API).
 *
 * Owns the stable prefix
 * `['agreements', 'getAgreementAthletes', <teamTwrn>, <agreementTwrn>]`.
 * View-specific trailing params (e.g. `{ limit: 5 }` on the agreement overview)
 * are appended by the read site after this prefix. Invalidations only ever need
 * the prefix, so pass fewer args to invalidate a broader slice of the cache.
 */
export const getAgreementAthletesQueryKey = (
  teamTwrn?: string | null,
  agreementTwrn?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.GET_AGREEMENT_ATHLETES];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (agreementTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAgreementAthletesQueryKey] agreementTwrn was provided without teamTwrn. teamTwrn is required when agreementTwrn is passed.',
        {
          agreementTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(agreementTwrn);
  }

  return key;
};

/**
 * Cache key for agreement statistics (`getAgreementStats` API).
 */
export const getAgreementStatsQueryKey = (
  teamTwrn?: string | null,
  agreementTwrn?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.GET_AGREEMENT_STATS];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (agreementTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAgreementStatsQueryKey] agreementTwrn was provided without teamTwrn. teamTwrn is required when agreementTwrn is passed.',
        {
          agreementTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(agreementTwrn);
  }

  return key;
};

/** Invalidation Helpers */

type InvalidateOptions = {
  queryClient?: QueryClient;
};

/**
 * Invalidates the agreement overview screen cache:
 * details, athletes (prefix), and stats for the given agreement.
 *
 * Over-invalidation of athletes is intentional — view-specific trailing params
 * (e.g. `{ limit: 5 }`) are covered by prefix matching on the stable key.
 */
export const invalidateAgreementOverview = (
  teamTwrn: string,
  agreementTwrn: string,
  options?: InvalidateOptions,
) => {
  const client = options?.queryClient ?? defaultQueryClient;

  return Promise.all([
    client.invalidateQueries({ queryKey: getAgreementDetailsQueryKey(teamTwrn, agreementTwrn) }),
    client.invalidateQueries({ queryKey: getAgreementAthletesQueryKey(teamTwrn, agreementTwrn) }),
    client.invalidateQueries({ queryKey: getAgreementStatsQueryKey(teamTwrn, agreementTwrn) }),
  ]);
};

/**
 * Invalidates both agreement-list caches for a team: the paginated table
 * (`getAgreementsList`) and the unpaginated select/dropdown list (`getAgreements`).
 * These back separate API calls with separate keys, so both need invalidating whenever
 * an agreement is created, canceled, or deleted — otherwise dropdowns like
 * `AgreementSelect` keep serving stale agreement data.
 */
export const invalidateAgreementsLists = (
  teamTwrn?: string | null,
  options?: InvalidateOptions,
) => {
  const client = options?.queryClient ?? defaultQueryClient;

  return Promise.all([
    client.invalidateQueries({ queryKey: getAgreementsListQueryKey(teamTwrn) }),
    client.invalidateQueries({ queryKey: getAgreementsQueryKey(teamTwrn) }),
  ]);
};
